import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, RotateCw } from "lucide-react";
import ArtifactDescriptionEditor from "@/components/evidenceGathering/ArtifactDescriptionEditor";
import ArtifactStatusChip from "@/components/evidenceGathering/ArtifactStatusChip";
import ArtifactThumbnail from "@/components/evidenceGathering/ArtifactThumbnail";
import ArtifactTypeBadge from "@/components/evidenceGathering/ArtifactTypeBadge";
import ArtifactVersionBadge from "@/components/evidenceGathering/ArtifactVersionBadge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { groupArtifactsBySheet, isFromLatestUpload, isTableArtifact } from "@/lib/artifacts";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactReviewListProps {
  artifacts: Artifact[];
  projectId: string;
  /** Offered per row only when the workbook is still to hand — re-extracting
   *  needs the file, which the upload dialog has and a later visit does not. */
  onRetry?: (artifact: Artifact) => void;
  retryingUids?: Set<string>;
  /** The project's highest upload version. Passed in rather than taken from
   *  `artifacts`, which is only the slice this upload produced — an upload that
   *  changed nothing would otherwise highlight whichever of its rows happened
   *  to carry the highest number. */
  latestUploadVersion?: number;
}

/** The descriptions a model wrote, laid out for a person to check.
 *
 *  Mirrors what adding documents gives you: a row per item, a live chip saying
 *  where it has got to, and the text editable in place once it lands. The
 *  difference is underneath — documents need a bespoke poller because their job
 *  state lives in a separate table, whereas an artifact carries its status on
 *  its own row, so the list just renders whatever the artifacts query last
 *  returned and that query already polls.
 *
 *  Tables are reviewed like anything else. A table is summarised rather than
 *  split into rows, and that summary is the only thing retrieval matches it on,
 *  so it carries more weight than a chart's rather than less. There is no
 *  picture to check it against, so the row and column count stands in. */
const ArtifactReviewList = ({
  artifacts,
  projectId,
  onRetry,
  retryingUids,
  latestUploadVersion: latest = 0,
}: ArtifactReviewListProps) => {
  // One at a time, like the document form — a long description is hard to read
  // with three others competing for the space.
  const [expandedUid, setExpandedUid] = useState<string | null>(null);
  const autoExpanded = useRef(false);
  const groups = groupArtifactsBySheet(artifacts);

  // Open the first row as soon as there is one, so the list arrives showing a
  // description rather than a stack of closed headers. Guarded by a ref because
  // the rows keep arriving as the query polls, and re-opening a row the user
  // had deliberately closed would be worse than never opening one.
  const firstUid = groups[0]?.leaves[0]?.uid;
  useEffect(() => {
    if (!autoExpanded.current && firstUid) {
      autoExpanded.current = true;
      setExpandedUid(firstUid);
    }
  }, [firstUid]);

  if (artifacts.length === 0) return null;

  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <Fragment key={group.key}>
          <p className="text-xs font-semibold text-muted-foreground pt-1">
            {group.tabNumber === Number.MAX_SAFE_INTEGER
              ? group.label
              : `${group.tabNumber}. ${group.label}`}
          </p>

          {group.leaves.map((artifact) => {
            const isExpanded = expandedUid === artifact.uid;
            const retrying = retryingUids?.has(artifact.uid) ?? false;

            return (
              <Collapsible
                key={artifact.uid}
                open={isExpanded}
                onOpenChange={(open) => setExpandedUid(open ? artifact.uid : null)}
                className="bg-card border rounded-lg overflow-hidden"
              >
                <div className="flex items-center gap-2 px-3 py-2">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium truncate">{artifact.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{artifact.uid}</span>
                    </button>
                  </CollapsibleTrigger>

                  <ArtifactTypeBadge type={artifact.type} className="shrink-0" />
                  <ArtifactVersionBadge
                    uploadVersion={artifact.uploadVersion}
                    highlight={isFromLatestUpload(artifact, latest)}
                    className="shrink-0"
                  />
                  <ArtifactStatusChip artifact={artifact} className="shrink-0 w-44 justify-end" />

                  {onRetry && artifact.status === "Error" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 shrink-0"
                      disabled={retrying}
                      onClick={() => onRetry(artifact)}
                    >
                      {retrying ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCw className="w-3.5 h-3.5" />
                      )}
                      {retrying ? "Retrying…" : "Try again"}
                    </Button>
                  )}
                </div>

                <CollapsibleContent>
                  <div className="border-t px-3 py-3">
                    <div className="flex gap-4">
                      {/* Big enough to actually check the description against,
                          which is the whole point of reading them here. Renders
                          nothing for a table, so the editor takes the width. */}
                      <ArtifactThumbnail
                        artifact={artifact}
                        projectId={projectId}
                        className="w-80 h-56 bg-muted/30"
                      />
                      <div className="flex-1 min-w-0">
                        <ArtifactDescriptionEditor artifact={artifact} projectId={projectId} />
                        {isTableArtifact(artifact) &&
                          artifact.rowCount != null &&
                          artifact.columnCount != null && (
                            // A table has no picture to read the description
                            // against, so its size is the next best check that
                            // the summary is describing the right thing.
                            <p className="text-xs text-muted-foreground mt-2">
                              Extracted {artifact.rowCount}{" "}
                              {artifact.rowCount === 1 ? "row" : "rows"} ×{" "}
                              {artifact.columnCount}{" "}
                              {artifact.columnCount === 1 ? "column" : "columns"}.
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
};

export default ArtifactReviewList;
